/**
 * <pdp-lightbox> — tap-to-zoom gallery dialog for PDP.
 *
 * Wraps the existing .product-gallery markup. On click of a slide image
 * (or Enter on a focused slide) opens a fullscreen <dialog> showing the
 * image at full resolution with prev/next navigation. Pinch-zoom is
 * delegated to the browser via `touch-action: pinch-zoom` on the viewport.
 *
 * Pointer affordance: gallery images use `cursor: zoom-in`.
 *
 * The thumbnail strip still drives the main gallery slide externally;
 * the lightbox reads `product.media` from the rendered DOM so it stays
 * in sync without needing a separate data source.
 */
class PdpLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-lightbox]');
    this.image = this.querySelector('[data-lightbox-image]');
    this.count = this.querySelector('[data-lightbox-count]');
    this.closeBtn = this.querySelector('[data-lightbox-close]');
    this.prevBtn = this.querySelector('[data-lightbox-prev]');
    this.nextBtn = this.querySelector('[data-lightbox-next]');

    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.activeIndex = 0;

    if (!this.dialog || !this.slides.length) return;

    this.slides.forEach((slide, i) => {
      const img = slide.querySelector('img');
      if (!img) return;
      img.addEventListener('click', () => this.open(i));
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', img.alt || `View image ${i + 1} full size`);
      img.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(i); }
      });
    });

    this.closeBtn?.addEventListener('click', () => this.dialog.close());
    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    this.galleryMain = this.querySelector('.product-gallery__main');
    this.galleryPrev = this.querySelector('[data-gallery-prev]');
    this.galleryNext = this.querySelector('[data-gallery-next]');
    if (this.galleryMain && (this.galleryPrev || this.galleryNext)) {
      const scrollToIdx = (idx) => {
        const target = this.slides[(idx + this.slides.length) % this.slides.length];
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      };
      const currentIdx = () => {
        const x = this.galleryMain.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        this.slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - x);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      };
      this.galleryPrev?.addEventListener('click', () => scrollToIdx(currentIdx() - 1));
      this.galleryNext?.addEventListener('click', () => scrollToIdx(currentIdx() + 1));
    }

    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.step(1);
      else if (e.key === 'ArrowLeft') this.step(-1);
    });

    this._touchStartX = null;
    const vp = this.querySelector('[data-lightbox-viewport]');
    if (vp) {
      vp.addEventListener('touchstart', (e) => { this._touchStartX = e.touches[0].clientX; }, { passive: true });
      vp.addEventListener('touchend', (e) => {
        if (this._touchStartX == null) return;
        const dx = e.changedTouches[0].clientX - this._touchStartX;
        if (Math.abs(dx) > 60) this.step(dx < 0 ? 1 : -1);
        this._touchStartX = null;
      });
    }
  }

  open(index) {
    this.activeIndex = index;
    this.render();
    this.dialog.showModal();
  }

  step(delta) {
    const next = (this.activeIndex + delta + this.slides.length) % this.slides.length;
    this.activeIndex = next;
    this.render();
  }

  render() {
    const slide = this.slides[this.activeIndex];
    const img = slide?.querySelector('img');
    if (!img) return;
    const srcset = img.srcset || '';
    const fullSrc = this.bestSrc(img, srcset) || img.currentSrc || img.src;
    this.image.src = fullSrc;
    this.image.alt = img.alt || '';
    if (this.count) this.count.textContent = `${this.activeIndex + 1} / ${this.slides.length}`;
  }

  bestSrc(img, srcset) {
    if (!srcset) return null;
    const entries = srcset.split(',').map((e) => e.trim().split(/\s+/));
    let best = entries[0];
    let bestW = parseInt(best[1] || '0', 10);
    for (const [url, descriptor] of entries) {
      const w = parseInt(descriptor || '0', 10);
      if (w > bestW) { bestW = w; best = [url, descriptor]; }
    }
    return best[0];
  }
}

if (!customElements.get('pdp-lightbox')) {
  customElements.define('pdp-lightbox', PdpLightbox);
}
